// frontend/src/components/CostEstimation/CostEstimation.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spin, Empty, message, Modal, Input } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import EstimationSidebar from './EstimationSidebar';
import EstimationTable from './EstimationTable';
import EstimationFooter from './EstimationFooter';
import { getDefaultValues } from '../../config/estimationConfig';
import { estimationAPI, dataAPI } from '../../services/api';
import {
  transformSearchResultsToEstimationData,
  buildHierarchyTree,
  flattenHierarchy,
  parseCSVForMenge,
  getAllUniqueProjects,
  findProjectInRowSuggestions,
  normalizeEbkpCode,
  normalizeUnitKey,
  seriesForBkpCodes
} from '../../services/estimationService';
import {
  recalculateAllTotals,
  calculateGrandTotals,
  buildRowIndex,
  isLeafRow,
  collectDescendantIndices
} from '../../services/estimationCalc';
import './CostEstimation.css';

const CostEstimation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get data from navigation state
  const locationState = location.state || {};
  const {
    searchResults: initialSearchResults,
    filters: initialFilters,
    estimationId: loadedEstimationId,
    estimationName: loadedEstimationName,
    from: originPath
  } = locationState;

  // State
  const [projectInputs, setProjectInputs] = useState({});
  const [tableRows, setTableRows] = useState([]);
  const [totals, setTotals] = useState({
    grandTotal: 0,
    perGF: 0,
    perGV: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [masterProjectId, setMasterProjectId] = useState(null);
  const [masterProjectOptions, setMasterProjectOptions] = useState([]);
  const [csvUploadErrors, setCsvUploadErrors] = useState([]);

  // How many search hits the table shows before "weitere anzeigen"
  // (ESTIMATION_SEARCH_RESULT_LIMIT in backend/.env, default 5)
  const [searchResultLimit, setSearchResultLimit] = useState(5);

  // Inflation (ZIW index) state — the per-row year lives on each row (manualYear)
  const [inflationBases, setInflationBases] = useState([]);
  const [inflationBasis, setInflationBasis] = useState(null);

  // SIA 416 Mengen import state (parsed workbook, session-only)
  const [mengenSheets, setMengenSheets] = useState([]);
  const [mengenSheet, setMengenSheet] = useState(null);
  
  // Persistence state
  const [currentEstimationId, setCurrentEstimationId] = useState(loadedEstimationId || null);
  const [currentEstimationName, setCurrentEstimationName] = useState(loadedEstimationName || null);
  const [searchResults, setSearchResults] = useState(initialSearchResults || null);
  const [filters, setFilters] = useState(initialFilters || null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  const isInitialMount = useRef(true);

  // Latest state for the table callbacks. The callbacks below are wrapped in
  // useCallback so their identity stays stable across renders (that is what
  // lets the table skip re-rendering all rows on every edit); they read the
  // current values through these refs instead of through a stale closure.
  const tableRowsRef = useRef(tableRows);
  const projectInputsRef = useRef(projectInputs);
  tableRowsRef.current = tableRows;
  projectInputsRef.current = projectInputs;

  /**
   * Attach the inflation context to every row: the index series of the
   * selected basis (chosen per row via its mapped BKP codes) and the latest
   * data point. The actual factor is computed per row from its Jahr value
   * (selected project's Baudatum, manually overridable):
   * index(latest) / index(project year).
   * Context null when no basis is selected (prices come directly from the search).
   */
  const applyInflationFactors = (rows, basis, bases) => {
    const base = basis ? (bases || []).find((b) => b.basis === basis) : null;
    if (!base || !base.series) {
      return rows.map((row) => ({ ...row, inflationContext: null }));
    }
    return rows.map((row) => {
      const seriesKey = seriesForBkpCodes(row.bkpCodes);
      const seriesValues = base.series[seriesKey] && Object.keys(base.series[seriesKey]).length > 0
        ? base.series[seriesKey]
        : (base.series.gesamt || {});
      return {
        ...row,
        inflationContext: {
          values: seriesValues,
          latestYear: base.latest_year,
          basisYear: base.basis_year
        }
      };
    });
  };

  /**
   * Mark every row as leaf / parent in one pass over the hierarchy index,
   * instead of rescanning all rows for each row.
   */
  const markLeafNodes = (rows) => {
    const index = buildRowIndex(rows);
    rows.forEach(row => {
      row.isLeaf = isLeafRow(row, index);
    });
  };

  /**
   * Load saved estimation
   */
  const loadSavedEstimation = async (estimationId, catalog = [], bases = []) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading saved estimation:', estimationId);

      // Get saved estimation
      const response = await estimationAPI.get(estimationId);
      const savedEstimation = response.data;

      console.log('Loaded estimation:', savedEstimation);

      // Set basic info
      setCurrentEstimationId(savedEstimation.id);
      setCurrentEstimationName(savedEstimation.name);
      setMasterProjectId(savedEstimation.master_project_id);

      // Re-run search with saved filters
      console.log('Re-running search with filters:', savedEstimation.search_filters);
      const searchResponse = await dataAPI.search(savedEstimation.search_filters);
      const freshSearchResults = searchResponse.data;

      console.log('Fresh search results:', freshSearchResults);

      // The search can answer 200 with success:false (e.g. the search service is
      // not reachable). The sheet then still builds from the catalogue, but with
      // no reference prices at all — say so instead of showing an empty table
      // that looks like the estimation lost its data.
      if (freshSearchResults && freshSearchResults.success === false) {
        message.warning(
          freshSearchResults.message ||
          'Die Referenzprojekte konnten nicht geladen werden. Ihre erfassten Werte bleiben erhalten.'
        );
      }

      setSearchResults(freshSearchResults);
      setFilters(savedEstimation.search_filters);

      // Restore project inputs (incl. the saved inflation basis, if any)
      setProjectInputs(savedEstimation.project_inputs);
      const savedBasis = savedEstimation.project_inputs?.inflationBasis || null;
      setInflationBasis(savedBasis);

      // Transform fresh search results onto the full catalogue
      const estimationData = transformSearchResultsToEstimationData(freshSearchResults, catalog);
      const tree = buildHierarchyTree(estimationData);
      const flatResult = flattenHierarchy(tree);
      const flatRows = flatResult.flat || flatResult;

      // Initialize rows with defaults
      const initialRows = flatRows.map(row => ({
        ...row,
        selectedSuggestionIndex: null,
        manualAmount: 0,
        manualCostPerUnit: null,
        manualYear: null,
        includedInTotal: false,
        calculatedTotal: 0,
        isLeaf: false,
        hasInconsistency: false,
        isBookmarked: false
      }));

      // Mark leaf nodes
      markLeafNodes(initialRows);

      // Apply saved row states (older estimations stored unpadded codes —
      // normalize so they land on the catalogue rows)
      const savedRowsMap = {};
      savedEstimation.rows.forEach(savedRow => {
        savedRowsMap[normalizeEbkpCode(savedRow.ebkp_code)] = {
          rowData: savedRow.row_data,
          isBookmarked: savedRow.is_bookmarked
        };
      });

      console.log('Saved rows map:', savedRowsMap);

      // Merge saved states with fresh rows
      const mergedRows = initialRows.map(row => {
        const savedState = savedRowsMap[row.ebkpCode];

        if (savedState) {
          const { rowData, isBookmarked } = savedState;

          // If bookmarked, lock the row completely - don't update anything
          if (isBookmarked) {
            console.log(`Row ${row.ebkpCode} is bookmarked - locking state`);
            return {
              ...row,
              ...rowData,
              isBookmarked: true,
              // Keep all saved values exactly as they were
              suggestions: row.suggestions // Keep fresh suggestions for reference but values are locked
            };
          }

          // Not bookmarked - apply saved state but allow updates from fresh data
          console.log(`Row ${row.ebkpCode} is not bookmarked - applying saved state`);
          return {
            ...row,
            selectedSuggestionIndex: rowData.selectedSuggestionIndex,
            manualAmount: rowData.manualAmount,
            manualCostPerUnit: rowData.manualCostPerUnit,
            manualYear: rowData.manualYear ?? null,
            includedInTotal: rowData.includedInTotal,
            isBookmarked: false
          };
        }

        // No saved state - use defaults
        return row;
      });

      console.log('Merged rows:', mergedRows);

      // Recalculate all totals (with the saved inflation basis applied)
      const rowsWithFactors = applyInflationFactors(mergedRows, savedBasis, bases);
      const rowsWithTotals = recalculateAllTotals(rowsWithFactors);
      setTableRows(rowsWithTotals);

      // Calculate grand totals
      const newGrandTotals = calculateGrandTotals(rowsWithTotals, savedEstimation.project_inputs);
      setTotals(newGrandTotals);

      // Get unique projects for master selector
      const uniqueProjects = getAllUniqueProjects(rowsWithTotals);
      setMasterProjectOptions(uniqueProjects);

      setLastSaveTime(new Date(savedEstimation.updated_at));
      setHasUnsavedChanges(false);

      message.success(`Kostenschätzung "${savedEstimation.name}" geladen`);

    } catch (error) {
      console.error('Error loading estimation:', error);
      setError('Fehler beim Laden der Kostenschätzung');
      message.error('Fehler beim Laden der Kostenschätzung');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize from fresh search or load saved estimation
   */
  useEffect(() => {
    const initialize = async () => {
      // Load the eBKP catalogue and ZIW index bases first — both paths need them
      let catalog = [];
      let bases = [];
      try {
        const [catalogResponse, basesResponse, configResponse] = await Promise.all([
          dataAPI.getCodeCatalog(),
          dataAPI.getInflationBases(),
          dataAPI.getConfig()
        ]);
        catalog = catalogResponse.data.codes || [];
        bases = basesResponse.data.bases || [];
        const limit = configResponse.data?.estimationSearchResultLimit;
        if (Number.isFinite(limit) && limit > 0) setSearchResultLimit(limit);
      } catch (e) {
        console.error('Could not load code catalogue / index bases / config:', e);
      }
      setInflationBases(bases);

      // Check if we're loading a saved estimation
      if (loadedEstimationId) {
        await loadSavedEstimation(loadedEstimationId, catalog, bases);
        return;
      }

      // Fresh estimation from search
      if (!initialSearchResults || !initialFilters) {
        console.log('No search results or filters, redirecting...');
        navigate('/search');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Initializing fresh estimation');
        console.log('Search Results:', initialSearchResults);
        console.log('Filters:', initialFilters);

        setSearchResults(initialSearchResults);
        setFilters(initialFilters);

        // Set default project inputs
        const defaults = getDefaultValues(initialFilters);
        setProjectInputs(defaults);

        // Transform search results onto the full catalogue
        const estimationData = transformSearchResultsToEstimationData(initialSearchResults, catalog);
        const tree = buildHierarchyTree(estimationData);
        const flatResult = flattenHierarchy(tree);
        const flatRows = flatResult.flat || flatResult;

        if (!Array.isArray(flatRows)) {
          throw new Error('Invalid data structure returned from flattenHierarchy');
        }

        // Initialize rows
        const initialRows = flatRows.map(row => ({
          ...row,
          selectedSuggestionIndex: null,
          manualAmount: 0,
          manualCostPerUnit: null,
          includedInTotal: false,
          calculatedTotal: 0,
          isLeaf: false,
          hasInconsistency: false,
          isBookmarked: false
        }));

        // Mark leaf nodes
        markLeafNodes(initialRows);

        // Calculate totals
        const rowsWithTotals = recalculateAllTotals(initialRows);
        setTableRows(rowsWithTotals);

        // Calculate grand totals
        const initialGrandTotals = calculateGrandTotals(rowsWithTotals, defaults);
        setTotals(initialGrandTotals);

        // Get unique projects
        const uniqueProjects = getAllUniqueProjects(rowsWithTotals);
        setMasterProjectOptions(uniqueProjects);

        setHasUnsavedChanges(false);

      } catch (error) {
        console.error('Error initializing cost estimation:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []); // Run only once on mount

  /**
   * Auto-save functionality - every 5 seconds
   */
  useEffect(() => {
    // Skip auto-save on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if no changes or no estimation name yet
    if (!hasUnsavedChanges || !currentEstimationName) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for 5 seconds
    autoSaveTimerRef.current = setTimeout(() => {
      handleSave(true); // true = silent auto-save
    }, 5000);

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, tableRows, projectInputs, totals, masterProjectId]);

  /**
   * Mark as having unsaved changes when data changes
   */
  useEffect(() => {
    if (!isInitialMount.current && currentEstimationName) {
      setHasUnsavedChanges(true);
    }
  }, [tableRows, projectInputs, totals, masterProjectId]);

  /**
   * Save estimation
   */
  const handleSave = async (isSilent = false) => {
    try {
      // If no name yet, ask for one
      if (!currentEstimationName) {
        Modal.confirm({
          title: 'Kostenschätzung speichern',
          content: (
            <div>
              <p>Geben Sie einen Namen für diese Kostenschätzung ein:</p>
              <Input
                id="estimation-name-input"
                placeholder="z.B. Projekt ABC - Phase 1"
                autoFocus
              />
            </div>
          ),
          okText: 'Speichern',
          cancelText: 'Abbrechen',
          onOk: async () => {
            const nameInput = document.getElementById('estimation-name-input');
            const name = nameInput?.value?.trim();

            if (!name) {
              message.warning('Bitte geben Sie einen Namen ein');
              return Promise.reject();
            }

            await saveEstimation(name, isSilent);
          }
        });
        return;
      }

      // Has name - save directly
      await saveEstimation(currentEstimationName, isSilent);

    } catch (error) {
      console.error('Error in handleSave:', error);
      if (!isSilent) {
        message.error('Fehler beim Speichern');
      }
    }
  };

  /**
   * Actual save logic
   */
  const saveEstimation = async (name, isSilent = false) => {
    try {
      setIsSaving(true);

      // Prepare row data
      const rowsToSave = tableRows.map(row => ({
        ebkp_code: row.ebkpCode,
        is_bookmarked: row.isBookmarked || false,
        row_data: {
          level: row.level,
          name: row.name,
          unit: row.unit,
          suggestions: row.suggestions,
          selectedSuggestionIndex: row.selectedSuggestionIndex,
          manualAmount: row.manualAmount,
          manualCostPerUnit: row.manualCostPerUnit,
          manualYear: row.manualYear ?? null,
          includedInTotal: row.includedInTotal,
          calculatedTotal: row.calculatedTotal,
          isLeaf: row.isLeaf,
          hasInconsistency: row.hasInconsistency,
          parentPath: row.parentPath
        }
      }));

      const saveData = {
        name: name,
        estimation_id: currentEstimationId,
        username: user?.username || 'unknown',
        search_filters: filters,
        project_inputs: { ...projectInputs, inflationBasis: inflationBasis },
        totals: totals,
        master_project_id: masterProjectId,
        rows: rowsToSave
      };

      console.log('Saving estimation:', saveData);

      const response = await estimationAPI.save(saveData);

      if (response.data.success) {
        setCurrentEstimationId(response.data.estimation_id);
        setCurrentEstimationName(name);
        setLastSaveTime(new Date());
        setHasUnsavedChanges(false);

        if (!isSilent) {
          message.success('Kostenschätzung erfolgreich gespeichert');
        }
      }

    } catch (error) {
      console.error('Error saving estimation:', error);
      
      if (error.response?.status === 409) {
        message.error('Eine Kostenschätzung mit diesem Namen existiert bereits');
      } else if (!isSilent) {
        message.error('Fehler beim Speichern der Kostenschätzung');
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle project input change
   */
  const handleInputChange = (fieldKey, value) => {
    const newInputs = {
      ...projectInputs,
      [fieldKey]: value
    };
    setProjectInputs(newInputs);

    if (fieldKey === 'gf' || fieldKey === 'gv') {
      const newGrandTotals = calculateGrandTotals(tableRows, newInputs);
      setTotals(newGrandTotals);
    }
  };

  /**
   * Handle inflation basis change: re-index all search-derived Preis/Einheit
   * values from each row's Jahr (selected project's year, overridable) to the
   * latest known data point.
   */
  const handleInflationBasisChange = (basis) => {
    const newBasis = basis || null;
    setInflationBasis(newBasis);

    const rowsWithFactors = applyInflationFactors(tableRows, newBasis, inflationBases);
    const updatedRows = recalculateAllTotals(rowsWithFactors);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputs);
    setTotals(newGrandTotals);
  };

  /**
   * Handle table row change.
   *
   * Addressed by eBKP code, not by position: the table shows a collapsed
   * subset of the rows, so its row index is no longer an index into the full
   * set. Codes are unique — the table already keys its rows on them.
   */
  const handleRowChange = useCallback((ebkpCode, updates) => {
    const currentRows = tableRowsRef.current;
    const rowIndex = buildRowIndex(currentRows).indexByCode.get(ebkpCode);
    if (rowIndex === undefined) return;

    const newRows = [...currentRows];
    const changedRow = newRows[rowIndex];

    newRows[rowIndex] = {
      ...changedRow,
      ...updates
    };

    // If includedInTotal changed, cascade to descendants
    if ('includedInTotal' in updates) {
      const index = buildRowIndex(newRows);
      const descendantIndices = collectDescendantIndices(changedRow.ebkpCode, newRows, index);
      descendantIndices.forEach(descIndex => {
        newRows[descIndex] = {
          ...newRows[descIndex],
          includedInTotal: updates.includedInTotal
        };
      });
    }

    // Recalculate totals
    const updatedRows = recalculateAllTotals(newRows);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputsRef.current);
    setTotals(newGrandTotals);
  }, []);

  /**
   * Handle toggle all
   */
  const handleToggleAll = useCallback(() => {
    const currentRows = tableRowsRef.current;
    const topLevelNodes = currentRows.filter(row => row.level === 1);
    const allTopLevelIncluded = topLevelNodes.every(row => row.includedInTotal === true);
    const newInclusionState = !allTopLevelIncluded;

    const newRows = currentRows.map(row => ({
      ...row,
      includedInTotal: newInclusionState
    }));

    const updatedRows = recalculateAllTotals(newRows);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputsRef.current);
    setTotals(newGrandTotals);
  }, []);

  /**
   * Handle master project change
   */
  const handleMasterProjectChange = (projectId) => {
    setMasterProjectId(projectId);

    if (projectId === null || projectId === undefined) {
      return;
    }

    const newRows = [...tableRows];
    let updatedCount = 0;
    let skippedManualCount = 0;
    let skippedNoProjectCount = 0;

    newRows.forEach((row, index) => {
      if (row.level === 1) return;
      if (row.manualCostPerUnit !== null && row.manualCostPerUnit !== undefined) {
        skippedManualCount++;
        return;
      }

      const suggestionIndex = findProjectInRowSuggestions(row, projectId);

      if (suggestionIndex !== -1) {
        newRows[index] = {
          ...row,
          selectedSuggestionIndex: suggestionIndex,
          manualCostPerUnit: null
        };
        updatedCount++;
      } else {
        skippedNoProjectCount++;
      }
    });

    const updatedRows = recalculateAllTotals(newRows);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputs);
    setTotals(newGrandTotals);

    message.success(
      `Master Projekt angewendet: ${updatedCount} ${updatedCount === 1 ? 'Zeile' : 'Zeilen'} aktualisiert`
    );

    if (skippedManualCount > 0) {
      message.info(
        `${skippedManualCount} ${skippedManualCount === 1 ? 'Zeile' : 'Zeilen'} mit manuellen Werten übersprungen`
      );
    }
  };

  /**
   * Apply one sheet of the parsed SIA 416 workbook: every metric's
   * Gesamttotal value becomes the Menge of all rows whose unit matches
   * (e.g. metric GSF/m2 -> rows with unit "m2 GSF"). Explicitly overrides
   * manual Menge edits; only bookmarked (locked) rows are skipped.
   */
  const applyMengenSheet = (sheetName, sheets) => {
    const sheet = (sheets || mengenSheets).find(s => s.name === sheetName);
    if (!sheet) return;

    const valueByUnit = {};
    sheet.metrics.forEach(metric => {
      valueByUnit[normalizeUnitKey(`${metric.unit} ${metric.abbrev}`)] = metric.value;
    });

    let updatedCount = 0;
    let skippedBookmarked = 0;
    const newRows = tableRows.map(row => {
      if (row.level === 1) return row;
      const value = valueByUnit[normalizeUnitKey(row.unit)];
      if (value === undefined) return row;
      if (row.isBookmarked) {
        skippedBookmarked++;
        return row;
      }
      updatedCount++;
      return { ...row, manualAmount: value };
    });

    const updatedRows = recalculateAllTotals(newRows);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputs);
    setTotals(newGrandTotals);

    message.success(
      `Blatt "${sheetName}": ${updatedCount} ${updatedCount === 1 ? 'Menge' : 'Mengen'} übernommen`
    );
    if (skippedBookmarked > 0) {
      message.info(
        `${skippedBookmarked} gesperrte ${skippedBookmarked === 1 ? 'Zeile' : 'Zeilen'} (Lesezeichen) übersprungen`
      );
    }
  };

  /**
   * Handle SIA 416 workbook upload: parse on the backend, select the
   * default sheet (Gesamttotal) and apply it.
   */
  const handleMengenFileUpload = async (file) => {
    try {
      const response = await estimationAPI.parseMengen(file);
      const sheets = response.data.sheets || [];
      if (sheets.length === 0) {
        message.warning('Keine Mengen in der Datei gefunden');
        return;
      }
      const defaultSheet = sheets.find(s => s.name === 'Gesamttotal')?.name || sheets[0].name;
      setMengenSheets(sheets);
      setMengenSheet(defaultSheet);
      setCsvUploadErrors([]);
      applyMengenSheet(defaultSheet, sheets);
    } catch (error) {
      console.error('Error parsing Mengen workbook:', error);
      message.error(error.response?.data?.detail || 'Fehler beim Lesen der Excel-Datei');
    }
  };

  /**
   * Handle sheet selection change: re-applies the sheet's Mengen
   * (overriding manual edits, by design).
   */
  const handleMengenSheetChange = (sheetName) => {
    setMengenSheet(sheetName);
    applyMengenSheet(sheetName);
  };

  /**
   * Handle CSV upload
   */
  const handleCSVUpload = (csvContent) => {
    const { success, skipped } = parseCSVForMenge(csvContent);

    if (success.length === 0 && skipped.length === 0) {
      message.warning('CSV-Datei ist leer');
      setCsvUploadErrors([]);
      return;
    }

    const newRows = [...tableRows];
    let updatedCount = 0;
    const notFoundCodes = [];

    success.forEach(({ code, amount }) => {
      // CSVs may use either spelling (A1.1 / A01.01) — same code
      const normalizedCode = normalizeEbkpCode(code);
      const rowIndex = newRows.findIndex(row => row.ebkpCode === normalizedCode);

      if (rowIndex !== -1) {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          manualAmount: amount
        };
        updatedCount++;
      } else {
        notFoundCodes.push(`Code "${code}" nicht in Tabelle gefunden`);
      }
    });

    const allErrors = [...skipped, ...notFoundCodes];
    setCsvUploadErrors(allErrors);

    const updatedRows = recalculateAllTotals(newRows);
    setTableRows(updatedRows);

    const newGrandTotals = calculateGrandTotals(updatedRows, projectInputs);
    setTotals(newGrandTotals);

    if (updatedCount > 0) {
      message.success(
        `${updatedCount} ${updatedCount === 1 ? 'Menge' : 'Mengen'} erfolgreich importiert`
      );
    }

    if (allErrors.length > 0) {
      message.warning(
        `${allErrors.length} ${allErrors.length === 1 ? 'Fehler' : 'Fehler'} beim Import (siehe Sidebar)`
      );
    }
  };

  /**
   * Handle export
   */
  const handleExport = () => {
    try {
      const headers = [
        'Level',
        'Included',
        'Bookmarked',
        'eBKP Code',
        'Element',
        'Type',
        'Einheit',
        'Projekt',
        'Menge',
        'Preis (CHF)',
        'Total (CHF)',
        'Inconsistency'
      ];

      const rows = tableRows.map(row => {
        let amount = row.manualAmount !== null && row.manualAmount !== undefined ? row.manualAmount : 0;
        let costPerUnit = 0;
        let projectName = '—';

        if (row.isLeaf || row.level > 1) {
          amount = row.manualAmount !== null && row.manualAmount !== undefined ? row.manualAmount : 0;

          if (row.manualCostPerUnit !== null && row.manualCostPerUnit !== undefined) {
            costPerUnit = row.manualCostPerUnit;
          } else if (row.selectedSuggestionIndex !== null &&
                     row.selectedSuggestionIndex !== undefined &&
                     row.suggestions &&
                     row.suggestions[row.selectedSuggestionIndex]) {
            costPerUnit = row.suggestions[row.selectedSuggestionIndex].costPerUnit || 0;
            projectName = row.suggestions[row.selectedSuggestionIndex].projectName;
          }
        }

        return [
          row.level,
          row.includedInTotal === true ? 'Ja' : 'Nein',
          row.isBookmarked === true ? 'Ja' : 'Nein',
          row.ebkpCode,
          row.name,
          row.isLeaf ? 'Leaf' : 'Parent',
          row.unit,
          projectName,
          (row.isLeaf || row.level > 1) ? amount : '',
          (row.isLeaf || row.level > 1) ? costPerUnit : '',
          row.calculatedTotal || 0,
          row.hasInconsistency ? 'WARNING' : ''
        ];
      });

      rows.push([
        '',
        '',
        '',
        '',
        'GRAND TOTAL (Level 1 only)',
        '',
        '',
        '',
        '',
        '',
        totals.grandTotal,
        ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const filename = currentEstimationName
        ? `${currentEstimationName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`
        : `kostenschaetzung_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('CSV exportiert');

    } catch (error) {
      console.error('Error exporting CSV:', error);
      message.error('Fehler beim Exportieren');
    }
  };

  /**
   * Go back to the page the user came from (agent chat or classic search)
   */
  const handleGoBack = () => {
    const target = originPath || '/search';
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Ungespeicherte Änderungen',
        content: 'Sie haben ungespeicherte Änderungen. Möchten Sie wirklich zurück zur Suche?',
        okText: 'Ja, zurück',
        cancelText: 'Abbrechen',
        onOk: () => {
          navigate(target);
        }
      });
    } else {
      navigate(target);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="estimation-loading">
        <Spin size="large" />
        <div className="estimation-loading-text">
          Kostenschätzung wird vorbereitet...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="estimation-empty">
        <Empty
          description={
            <span>
              Fehler beim Laden der Kostenschätzung:
              <br />
              {error}
            </span>
          }
        />
        <Button type="primary" onClick={handleGoBack}>
          Zurück zur Suche
        </Button>
      </div>
    );
  }

  // No data state
  if (!searchResults || tableRows.length === 0) {
    return (
      <div className="estimation-empty">
        <Empty
          description={
            <span>
              Keine Daten für Kostenschätzung verfügbar.
              <br />
              Bitte führen Sie zuerst eine Suche durch.
            </span>
          }
        />
        <Button type="primary" onClick={handleGoBack}>
          Zurück zur Suche
        </Button>
      </div>
    );
  }

  return (
    <div className="cost-estimation-container">
      {/* Sidebar */}
      <div className="cost-estimation-sidebar">
        <EstimationSidebar
          projectInputs={projectInputs}
          onInputChange={handleInputChange}
          masterProjectOptions={masterProjectOptions}
          masterProjectId={masterProjectId}
          onMasterProjectChange={handleMasterProjectChange}
          inflationBases={inflationBases}
          inflationBasis={inflationBasis}
          onInflationBasisChange={handleInflationBasisChange}
          onCSVUpload={handleCSVUpload}
          onMengenFileUpload={handleMengenFileUpload}
          mengenSheets={mengenSheets}
          mengenSheet={mengenSheet}
          onMengenSheetChange={handleMengenSheetChange}
          csvUploadErrors={csvUploadErrors}
          onSave={handleSave}
          isSaving={isSaving}
          lastSaveTime={lastSaveTime}
          hasUnsavedChanges={hasUnsavedChanges}
          estimationName={currentEstimationName}
        />
      </div>

      {/* Main Content */}
      <div className="cost-estimation-main">
        {/* Header */}
        <div className="cost-estimation-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleGoBack}
              type="text"
            >
              Zurück zur Suche
            </Button>
            <h1>
              Kostenschätzung
              {currentEstimationName && (
                <span style={{ fontSize: '16px', color: '#666', marginLeft: '12px' }}>
                  — {currentEstimationName}
                </span>
              )}
            </h1>
            {hasUnsavedChanges && (
              <span style={{ fontSize: '12px', color: '#fa8c16' }}>
                (Ungespeicherte Änderungen)
              </span>
            )}
          </div>

          <div className="cost-estimation-header-meta">
            {projectInputs.buildingType && (
              <span>Gebäudetyp: {Array.isArray(projectInputs.buildingType) ? projectInputs.buildingType[0] : projectInputs.buildingType}</span>
            )}
            {projectInputs.kanton && (
              <span>Kanton: {Array.isArray(projectInputs.kanton) ? projectInputs.kanton[0] : projectInputs.kanton}</span>
            )}
            {projectInputs.gf && (
              <span>GF: {projectInputs.gf} m²</span>
            )}
            {projectInputs.gv && (
              <span>GV: {projectInputs.gv} m³</span>
            )}
            <span>Projekte: {searchResults.total_projects || searchResults.results?.length || 0}</span>
            {lastSaveTime && (
              <span style={{ fontSize: '11px', color: '#999' }}>
                Zuletzt gespeichert: {lastSaveTime.toLocaleTimeString('de-CH')}
              </span>
            )}
          </div>
        </div>

        {/* Table Content */}
        <div className="cost-estimation-content">
          <EstimationTable
            rows={tableRows}
            onRowChange={handleRowChange}
            onToggleAll={handleToggleAll}
            storageKey={currentEstimationId || currentEstimationName || 'neu'}
            searchResultLimit={searchResultLimit}
          />
        </div>

        {/* Footer */}
        <EstimationFooter
          totals={totals}
          onExport={handleExport}
          onSave={() => handleSave(false)}
          isSaving={isSaving}
        />
      </div>
    </div>
  );
};

export default CostEstimation;